import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Fragment, useMemo } from "react";

import {
	getInvoiceStatusDisplayNameById,
	invoiceStatusId,
} from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatus } from "~/modules/billing/api/invoiceStatus/invoiceStatusIdTypes.ts";
import { getInvoiceStatusIcon } from "~/modules/billing/components/InvoicesIndexView/invoicesIndexUtils.tsx";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	className?: string
	invoiceStatuses: InvoiceStatus[];
	filterValues: string[];
	onChange: (newStatusFilter: string[]) => void;
};

const InvoiceStatusFilter = ({ onChange, invoiceStatuses, filterValues }: Props) => {
	const enrichedStatuses = useMemo(() => {
		if (invoiceStatuses.length > 0) {
			return invoiceStatuses.filter(invoiceStatus=>invoiceStatus.id !== invoiceStatusId.Pending).sort(byObjectProperty("order")).map(status => ({
				value: status.id,
				label: getInvoiceStatusDisplayNameById(status.id),
			}));
		}
		return [];
	}, [invoiceStatuses]);

	const handleStatusFilterChange = (statusId: string) => {
		if (filterValues.includes(statusId)) {
			onChange(filterValues.filter(id => id !== statusId));
		} else {
			onChange([...filterValues, statusId]);
		}
	};

	return <div className="isolate flow-root">
		<Popover.Group className="-mx-4 flex items-center divide-x divide-gray-200">
			<Popover className="relative inline-block px-4 text-left">
				<Popover.Button
					className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
					<span>Status Filter</span>
					{filterValues.length > 0 && (
						<span className="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
														{filterValues.length}
													</span>
					)}
					<ChevronDownIcon
						className="-mr-1 ml-1 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
						aria-hidden="true"
					/>
				</Popover.Button>

				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<Popover.Panel
						className="absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-none">
						<form className="space-y-4">
							{enrichedStatuses.map((option, index) => (
								<div key={option.value}
									 className="flex items-center">
									<input
										id={`filterValues-${index}`}
										name={`filterValues[]`}
										defaultValue={option.value}
										type="checkbox"
										defaultChecked={filterValues.includes(option.value)}
										className="size-4 cursor-pointer rounded border-gray-300 text-accent-600 accent-accent-500 focus:ring-accent-500"
										onChange={() => handleStatusFilterChange(option.value)}
									/>
									<label
										htmlFor={`filterValues-${index}`}
										className="ml-3 flex w-full cursor-pointer items-center gap-x-1 whitespace-nowrap pr-6 text-sm font-medium text-gray-900 hover:text-danger-500"
									>
										{getInvoiceStatusIcon(option.value, "size-5")} {option.label}
									</label>
								</div>
							))}
						</form>
					</Popover.Panel>
				</Transition>
			</Popover>
		</Popover.Group>
	</div>;
};

export default InvoiceStatusFilter;